<template>
    <section class="slice slice-trends-newsletter" :class="{'slice-trends-newsletter--grey': backgroundColor === 'grey'}">
        <div class="slice-content">
            <div class="trends-newsletter__content" :class="{'trends-newsletter__content--focused': isFocused}">
                <TitleBrand :field="slice.primary.title" class="title-brand" />
                <MktHubspotFormAnimated
                    :region="slice.primary.hubspotRegion || ''"
                    :portal-id="slice.primary.hubspotPortalId || ''"
                    :form-id="slice.primary.hubspotFormId || ''"
                    :salesforce-id="slice.primary.salesforceId || ''"
                    :visible-field-name="slice.primary.visibleFieldName || 'email'"
                    @focusin="onFocusIn"
                    @focusout="onFocusOut"
                    @submit="onFormSubmit"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import type {Content} from '@prismicio/client';
    import {getSliceComponentProps} from '@prismicio/vue';

    import {MktHubspotFormAnimated} from '#components';
    import {useDataTracking} from '#imports';
    import TitleBrand from '~/components/TitleBrand.vue';

    // The array passed to `getSliceComponentProps` is purely optional.
    // Consider it as a visual hint for you when templating your slice.
    const props = defineProps(getSliceComponentProps<Content.TrendsNewsletterSlice>(['slice', 'index', 'slices', 'context']));
    const backgroundColor = computed(() => props.slice.primary.backgroundColor);

    const {track, extractWebAttributionProperties} = useDataTracking();

    const isFocused = ref(false);

    function onFocusIn() {
        isFocused.value = true;
    }
    function onFocusOut() {
        isFocused.value = false;
    }

    function onFormSubmit(form: HTMLFormElement) {
        const h1 = document.querySelector('h1');
        const typeField = form.querySelector(`.hs-input[name=form_type]`) as HTMLInputElement;
        const eventProperties = {
            url: window.location.pathname,
            page_title_h1: h1?.innerText,
            page_category: 'resources-center',
            form_type: typeField?.value || null,
            form_name: form.dataset.formId,
        };
        const userProperties = {
            ...extractWebAttributionProperties(),
            referrer: document.referrer || null,
        };
        track('website_form_submitted', eventProperties, userProperties);
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-trends-newsletter {
        padding: 0;

        &--grey {
            background-color: var(--joy-color-neutral-20);
        }

        @include mq.screen_md(min) {
            padding: 0 var(--joy-core-spacing-6);
        }

        .slice-content {
            .trends-newsletter__content {
                background-color: var(--joy-color-primary-50);
                text-align: center;

                margin-bottom: var(--joy-core-spacing-12);
                padding: var(--joy-core-spacing-9) var(--joy-core-spacing-5);
                border-radius: var(--joy-core-radius-4);

                &,
                .title-brand,
                .title-brand * {
                    color: var(--joy-color-neutral-0);
                    font-size: 32px;
                    font-weight: var(--joy-font-weight-normal);
                    line-height: var(--joy-line-height-small);
                }

                .title-brand {
                    margin-bottom: var(--joy-core-spacing-5);
                }

                @include mq.screen_md(min) {
                    display: flex;
                    flex-direction: row;
                    text-align: left;
                    padding: var(--joy-core-spacing-9) var(--joy-core-spacing-12);

                    .title-brand {
                        width: 340px;
                        padding-right: var(--joy-core-spacing-4);
                        flex: none;
                        margin-bottom: 0;
                    }
                    .title-brand * {
                        font-size: var(--joy-font-size-secondary-400);
                    }

                    .hubspot-form-animated__wrapper {
                        flex: 1;
                    }

                    &:not(&--focused) {
                        .hubspot-form-animated__wrapper {
                            display: flex;
                            align-items: center;
                        }
                    }
                }

                @include mq.screen_lg(min) {
                    .title-brand {
                        width: 390px;
                        padding-right: var(--joy-core-spacing-6);
                    }
                }
            }
        }

        .hubspot-form.mkt-hubspot-form-animated {
            label,
            .hs-richtext,
            .hs-form-field .hs-error-msgs,
            .hs-main-font-element {
                &,
                * {
                    color: var(--joy-color-neutral-0) !important;
                }
            }
            .hs-form-field .hs-input {
                background-color: var(--joy-color-neutral-10);
                border-color: var(--joy-color-neutral-10);
            }
            .hs-fieldtype-text .hs-input,
            .hs-fieldtype-select .hs-input {
                height: 60px;
            }
            .hs-button {
                background-color: transparent;
                border-color: var(--joy-color-neutral-10);

                &:hover {
                    background-color: var(--joy-color-primary-70);
                }
            }
            .hs-main-font-element {
                font-family: var(--joy-font-family-base) !important;
                font-size: var(--joy-font-size-primary-400) !important;
                font-weight: var(--joy-font-weight-normal) !important;
                line-height: var(--joy-line-height-large) !important;
            }
        }
    }
</style>
